import { motion, useInView } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import myPhoto from '../assets/iam4.jpeg';

const phrases = ['DevOps Engineer ', 'Front-End Developer ', 'Data Analytics ', 'Software QA ', 'UI/UX Design '];

const Home = () => {
  const [text, setText] = useState('');
  const [index, setIndex] = useState(0);
  const [subIndex, setSubIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  const textRef = useRef(null);
  const imgRef = useRef(null);
  const isTextInView = useInView(textRef, { once: true });
  const isImgInView = useInView(imgRef, { once: true });

  useEffect(() => {
    if (index >= phrases.length) return;

    if (!deleting && subIndex === phrases[index].length) {
      setTimeout(() => setDeleting(true), 2000);
      return;
    }

    if (deleting && subIndex === 0) {
      setDeleting(false);
      setIndex((prev) => (prev + 1) % phrases.length);
      return;
    }

    const timeout = setTimeout(() => {
      setSubIndex((prev) => (deleting ? prev - 1 : prev + 1));
      setText(phrases[index].substring(0, subIndex));
    }, deleting ? 75 : 120);

    return () => clearTimeout(timeout);
  }, [subIndex, index, deleting]);

  return (
    <section
      id="home"
      className="relative flex flex-col min-h-screen overflow-hidden text-white bg-gradient-to-tr from-gray-900 via-slate-900 to-gray-900"
    >
      {/* Background Effects - consistent with other pages */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute bg-blue-500 rounded-full top-20 left-20 w-72 h-72 blur-3xl" />
        <div className="absolute bg-purple-500 rounded-full bottom-20 right-20 w-96 h-96 blur-3xl" />
      </div>
      
      {/* Grid Pattern - consistent with other pages */}
      <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-[0.02]" />

      <div className="relative z-10 flex flex-col items-center justify-center flex-grow w-full px-6 py-20 mx-auto max-w-7xl">
        <div className="flex flex-col items-center justify-between w-full gap-16 lg:flex-row">
          
          {/* Text Content */}
          <motion.div
            ref={textRef}
            initial={{ x: -100, opacity: 0 }}
            animate={isTextInView ? { x: 0, opacity: 1 } : { x: -100, opacity: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="space-y-8 text-center lg:w-1/2 lg:text-left"
          >
            {/* Status Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-400 border rounded-full bg-blue-500/10 border-blue-500/20"
            >
              <div className="w-2 h-2 mr-2 bg-blue-500 rounded-full animate-pulse" />
              Currently working as a DevOps Engineer
            </motion.div>

            <div className="space-y-4">
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-4xl font-bold leading-tight text-white md:text-5xl lg:text-7xl"
              >
                Hey, I'm Hugo
              </motion.h1>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="space-y-2"
              >
                <p className="text-xl lg:text-2xl font-semibold text-blue-400 min-h-[2.5rem] leading-snug">
                  {text}
                  <span className="text-blue-500 animate-pulse">|</span>
                </p>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="max-w-2xl text-lg leading-relaxed text-gray-300"
              >
                I focus on building responsive interfaces, analyzing data for insights, testing to ensure quality, and designing user-experiences that bring digital products to life.
              </motion.p>
            </div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
              className="flex flex-col gap-4 pt-4 sm:flex-row"
            >
              <motion.button
                onClick={() => document.getElementById('recent-works')?.scrollIntoView({ behavior: 'smooth' })}
                whileHover={{ scale: 1.05, boxShadow: "0 20px 25px -5px rgba(59, 130, 246, 0.3)" }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 font-semibold shadow-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 rounded-xl"
              >
                View My Work
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Profile Image */}
          <motion.div
            ref={imgRef}
            initial={{ x: 100, opacity: 0 }}
            animate={isImgInView ? { x: 0, opacity: 1 } : { x: 100, opacity: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="flex justify-center lg:w-1/2"
          >
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute inset-0 scale-110 rounded-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 blur-2xl" />
              
              {/* Profile Image */}
              <motion.div
                whileHover={{ scale: 1.05, rotate: 2 }}
                transition={{ type: "spring", stiffness: 300 }}
                className="relative overflow-hidden border-4 rounded-full shadow-2xl w-80 h-80 lg:w-96 lg:h-96 border-gray-700/50"
              >
                <img 
                  src={myPhoto} 
                  alt="Profile"
                  className="object-cover w-full h-full"
                />
              </motion.div>

              {/* Floating Elements */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                className="absolute flex items-center justify-center w-12 h-12 rounded-full shadow-lg -top-4 -right-4 bg-gradient-to-r from-blue-500 to-purple-500"
              >
                <span className="font-bold text-white">⚡</span>
              </motion.div>

              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute w-8 h-8 rounded-full shadow-lg -bottom-2 -left-2 bg-gradient-to-r from-green-500 to-blue-500"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        className="flex flex-col items-center pb-8 space-y-2"
      >
        <p className="text-sm text-gray-400">Scroll to explore</p>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown className="w-5 h-5 text-gray-400" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Home;